import { createSelector } from "reselect";
import { AppState } from "..";
import { CLOSED, OFFER_LATER, ON_OFFER } from "../../types/offerStates";
import { getClosingTime } from "./calculateClosingTimes/calculator";
import { getDayString } from "./calculateClosingTimes/utils";
import { isClosingTime } from "./isClosingTime";
import { restaurantSelector } from "./selectors";

export const selectRestaurantsOfferState = (state: AppState) =>
  state.restaurants.offerStates;

export const selectRestaurantOfferState = (key: string) => (state: AppState) =>
  state.restaurants.offerStates[key];

export const selectRestaurantDiscountString = (key: string) =>
  createSelector(
    selectRestaurantOfferState(key),
    restaurantSelector(key),
    (offerState, restaurant) => {
      if (!restaurant) {
        return "";
      }
      const dayOfWeek = getDayString(new Date().getDay());
      const closingTimeObj = restaurant.closingTimes[dayOfWeek];
      if (!isClosingTime(closingTimeObj)) {
        return "Restaurant closed today.";
      }
      const {
        discountPercent,
        discountStartingTime,
        closingTime
      } = getClosingTime(closingTimeObj);
      switch (offerState) {
        case CLOSED:
          return `${discountPercent}% ended at ${closingTime}`;
        case OFFER_LATER:
          return `${discountPercent}% off starts at ${discountStartingTime}`;
        case ON_OFFER:
          return `${discountPercent}% off from ${discountStartingTime} to ${closingTime}`;
        default:
          return "";
      }
    }
  );

export const selectRestaurantStartingTimeString = (key: string) =>
  createSelector(restaurantSelector(key), restaurant => {
    if (!restaurant || !restaurant.closingTimes) {
      return "";
    }
    const dayOfWeek = getDayString(new Date().getDay());
    const closingTime = restaurant.closingTimes[dayOfWeek];
    if (!isClosingTime(closingTime)) {
      return "";
    }
    const { discountStartingTime } = getClosingTime(closingTime);
    return discountStartingTime;
  });
